// ════════════════════════════════════════════════════════════════════════════
//  Contas a Pagar — tela OPERACIONAL ("o que eu pago hoje").
//
//  Paridade com o desktop: UMA LINHA POR PARCELA (contas_pagarbaixa), com
//  vencimento, valor e saldo DA PARCELA. O título (contas_pagar) é a obrigação
//  que agrupa — sua visão consolidada fica no drawer de detalhe.
//
//  Duas operações distintas, como no desktop:
//    • Pagar a PARCELA da linha (aceita valor parcial);
//    • Quitar o TÍTULO inteiro, no menu de ações da linha.
// ════════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from "react";
import {
  Plus, Calendar, DollarSign, AlertCircle, CheckCircle,
  Clock, Search, Pencil, Trash2, CheckCheck, RotateCcw,
  ChevronLeft, ChevronRight, ListChecks, Eye, X, Wallet,
} from "lucide-react";
import { useDebounce } from "@/hooks/useDebounce";
import { useParcelasPagar } from "@/features/financeiro/hooks/useContasPagar";
import { ContaPagarForm } from "@/features/financeiro/components/ContaPagarForm";
import { ContaPagarEditDialog } from "@/features/financeiro/components/ContaPagarEditDialog";
import { DeleteConfirmDialog } from "@/features/financeiro/components/DeleteConfirmDialog";
import { BaixaParcelaPagarDialog } from "@/features/financeiro/components/BaixaParcelaPagarDialog";
import { QuitarTituloDialog } from "@/features/financeiro/components/QuitarTituloDialog";
import { EstornoDialog } from "@/features/financeiro/components/EstornoDialog";
import { ContaPagarDrawer } from "@/features/financeiro/components/ContaPagarDrawer";
import { ColunaOrdenavel as Col } from "@/features/financeiro/components/ColunaOrdenavel";
import { PeriodoFiltro, usePeriodo } from "@/components/listas/PeriodoFiltro";
import { calcularStatus } from "@/features/financeiro/types";
import type { StatusConta, ContaPagarParcelaDto, StatusFiltro,
  OrdenarPorPagar, DirecaoOrdenacao,
  AtualizarContaPagarRequest } from "@/features/financeiro/types";
import type { ContaPagarFormData } from "@/features/financeiro/schemas/contaPagarSchema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { usePermissoes } from "@/contexts/PermissoesContext";
import { FORMULARIOS } from "@/domain/formularios";
import { FornecedorCombobox } from "@/components/FornecedorCombobox";

const PAGE_SIZES = [20, 50, 100];

const statusColors: Record<StatusConta, string> = {
  pago:      "bg-green-500/10 text-green-500 hover:bg-green-500/20",
  recebido:  "bg-green-500/10 text-green-500 hover:bg-green-500/20",
  pendente:  "bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500/20",
  atrasado:  "bg-red-500/10 text-red-500 hover:bg-red-500/20",
};
const statusLabels: Record<StatusConta, string> = {
  pago: "Pago", recebido: "Recebido", pendente: "Pendente", atrasado: "Atrasado",
};

const ContasPagar = () => {
  const { temPermissao } = usePermissoes();
  const podeIncluir = temPermissao(FORMULARIOS.CONTAS_PAGAR, "incluir");
  const podeEditar  = temPermissao(FORMULARIOS.CONTAS_PAGAR, "editar");
  const podeExcluir = temPermissao(FORMULARIOS.CONTAS_PAGAR, "excluir");

  const [pagina, setPagina]               = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);
  const [searchTerm, setSearchTerm]       = useState("");
  const [statusFilter, setStatusFilter]   = useState<StatusFiltro>("todos");
  // Período pelo VENCIMENTO. Abre no mês corrente INTEIRO (até o último dia):
  // o que vence ainda este mês é justamente o que a tela precisa mostrar.
  const periodo = usePeriodo("mes", undefined, { mesInteiro: true });
  const { dtInicio, dtFim } = periodo.intervalo;
  const [fornecedorId, setFornecedorId]   = useState<number | null>(null);
  const [open, setOpen]                   = useState(false);
  const [selectedIds, setSelectedIds]     = useState<number[]>([]);
  const [detalheId, setDetalheId]         = useState<number | null>(null);

  // Alvos das ações — parcela e título são coisas diferentes.
  const [baixando, setBaixando]                 = useState<ContaPagarParcelaDto | null>(null);
  const [estornoParcelaId, setEstornoParcelaId] = useState<number | null>(null);
  const [excluindo, setExcluindo]               = useState<ContaPagarParcelaDto | null>(null);
  const [quitandoTitulo, setQuitandoTitulo]     = useState<ContaPagarParcelaDto | null>(null);
  const [editandoTituloId, setEditandoTituloId] = useState<number | null>(null);

  // Padrão: vencimento CRESCENTE — o que vence (ou já venceu) primeiro no topo,
  // que é a ordem de trabalho de quem paga. Antes era pelo Id (ordem de cadastro).
  const [ordenarPor, setOrdenarPor] = useState<OrdenarPorPagar>("vencimento");
  const [direcao, setDirecao]       = useState<DirecaoOrdenacao>("asc");

  const hoje = new Date().toISOString().slice(0, 10);
  const buscaAplicada = useDebounce(searchTerm, 400);

  useEffect(() => {
    setPagina(1);
    setSelectedIds([]);
  }, [buscaAplicada, statusFilter, dtInicio, dtFim, fornecedorId, tamanhoPagina, ordenarPor, direcao]);

  function ordenarPorCampo(campo: OrdenarPorPagar) {
    if (campo === ordenarPor) setDirecao((d) => (d === "asc" ? "desc" : "asc"));
    else { setOrdenarPor(campo); setDirecao("desc"); }
  }
  const ordProps = { ordenarPor, direcao, onOrdenar: ordenarPorCampo };

  const {
    parcelas, total, totalPaginas, totais, isLoading,
    criarConta, isCriando,
    excluirTitulo, excluirParcela,
    baixarParcela, isBaixandoParcela,
    baixarParcelasLote, isBaixandoLote,
    estornarParcela, isEstornandoParcela,
    quitarTitulo, isQuitandoTitulo,
    atualizarConta, isAtualizando,
  } = useParcelasPagar({
    pagina, tamanhoPagina,
    busca: buscaAplicada,
    status: statusFilter,
    dtInicio, dtFim,
    incluirVencidos: true,
    fornecedorId: fornecedorId ?? undefined,
    ordenarPor, direcao,
  });

  const temFiltro = !!buscaAplicada || statusFilter !== "todos" || periodo.periodo !== "mes" || !!fornecedorId;
  function limparFiltros() {
    setSearchTerm(""); setStatusFilter("todos"); periodo.setPeriodo("mes"); setFornecedorId(null);
  }

  // ── KPIs (do conjunto filtrado inteiro, por parcela, vindos do servidor) ──
  const totalPagar    = totais.totalEmAberto;
  const totalPago     = totais.totalQuitado;
  const totalAtrasado = totais.totalAtrasado;

  // ── Seleção múltipla: só parcelas REAIS e em aberto ──────────────────────
  // Título órfão (semParcela) tem id 0 — não há parcela para o lote baixar.
  const selecionaveis = parcelas.filter((p) => !p.isPago && !p.semParcela && p.id > 0).map((p) => p.id);
  const allSelected   = selecionaveis.length > 0 && selecionaveis.every((id) => selectedIds.includes(id));
  const someSelected  = selecionaveis.some((id) => selectedIds.includes(id));

  function toggleAll() { setSelectedIds(allSelected ? [] : selecionaveis); }
  function toggleOne(id: number) {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  // ── Handlers ─────────────────────────────────────────────────────────────
  function handleCreate(data: ContaPagarFormData) {
    criarConta(
      {
        fornecedorId:    data.fornecedorId,
        centroCustoId:   data.centroCustoId,
        contaContabilId: data.contaContabilId,
        tipoDespesasId:  data.tipoDespesasId,
        grupoDespesasId: data.grupoDespesasId,
        dtEmissao:       data.dtEmissao || hoje,
        dtVencimento:    data.dtVencimento,
        valorParcela:    data.valorParcela,
        qtdParcelas:     data.qtdParcelas,
        diasParcelas:    data.diasParcelas || undefined,
        diaFixo:         data.diaFixo,
        numDocumento:    data.numDocumento,
        lancamento:      data.lancamento,
      },
      { onSuccess: () => setOpen(false) }
    );
  }

  // O diálogo já monta o request (decide se envia parcelamento conforme haja
  // pagamento, e omite classificação zerada) — aqui só repassamos.
  function handleUpdate(id: number, data: AtualizarContaPagarRequest) {
    atualizarConta(
      { id, data },
      { onSuccess: () => setEditandoTituloId(null) }
    );
  }

  function handleBaixarLote() {
    baixarParcelasLote(
      { ids: selectedIds, dtPagamento: hoje },
      { onSuccess: () => setSelectedIds([]) }
    );
  }

  const brl = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  return (
    <div className="p-6 space-y-6">
      {/* ── Cabeçalho ── */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Contas a Pagar</h1>
          <p className="text-muted-foreground mt-1">Uma linha por parcela — gerencie suas obrigações</p>
        </div>
        <div className="flex gap-2">
          {selectedIds.length > 0 && (
            <Button
              variant="secondary" className="gap-2"
              onClick={handleBaixarLote} disabled={isBaixandoLote}
            >
              <ListChecks className="h-4 w-4" />
              Pagar Selecionadas ({selectedIds.length})
            </Button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            {podeIncluir && (
              <DialogTrigger asChild>
                <Button className="gap-2">
                  <Plus className="h-4 w-4" /> Nova Conta
                </Button>
              </DialogTrigger>
            )}
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Nova Conta a Pagar</DialogTitle>
                <DialogDescription>
                  Informe o valor de UMA parcela e quantas parcelas — o título será
                  o valor × a quantidade. Campos com * são obrigatórios.
                </DialogDescription>
              </DialogHeader>
              <ContaPagarForm
                onSubmit={handleCreate}
                onCancel={() => setOpen(false)}
                isLoading={isCriando}
                defaultValues={{ dtEmissao: hoje, qtdParcelas: 1, diaFixo: false }}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* ── KPIs ── */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="h-4 w-4" /> Total a Pagar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{brl(totalPagar)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {totais.qtdEmAberto} parcela(s) em aberto
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle className="h-4 w-4" /> Total Pago
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{brl(totalPago)}</div>
            <p className="text-xs text-muted-foreground mt-1">{totais.qtdQuitado} parcela(s)</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertCircle className="h-4 w-4" /> Atrasado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{brl(totalAtrasado)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {totais.qtdAtrasado} parcela(s)
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Calendar className="h-4 w-4" /> Próximo Vencimento
            </CardTitle>
          </CardHeader>
          <CardContent>
            {totais.proximoVencimento ? (
              <>
                <div className="text-2xl font-bold">
                  {new Date(totais.proximoVencimento).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {brl(totais.proximoVencimentoValor ?? 0)}
                </p>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">Nenhuma parcela pendente</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Tabela ── */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle>Parcelas a Pagar</CardTitle>
              <span className="text-sm text-muted-foreground">({total} total)</span>
            </div>
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 w-full md:w-auto items-end">
              <div className="relative flex-1 md:w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por fornecedor, documento ou duplicata..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex flex-col w-full sm:w-64">
                <span className="text-[10px] text-muted-foreground leading-none mb-1">Fornecedor</span>
                <FornecedorCombobox value={fornecedorId} onChange={setFornecedorId} placeholder="Todos os fornecedores" />
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] text-muted-foreground leading-none mb-1">Vencimento</span>
                <div className="flex flex-col sm:flex-row gap-2">
                  <PeriodoFiltro estado={periodo} rotuloData="Vencimento" />
                </div>
                {periodo.periodo !== "todos" && (
                  <span className="text-[10px] text-muted-foreground mt-1">+ vencidos em aberto de antes do período</span>
                )}
              </div>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFiltro)}>
                <SelectTrigger className="w-full sm:w-36">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="pago">Pago</SelectItem>
                  <SelectItem value="atrasado">Atrasado</SelectItem>
                </SelectContent>
              </Select>
              <Select value={String(tamanhoPagina)} onValueChange={(v) => setTamanhoPagina(Number(v))}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              {temFiltro && (
                <Button variant="ghost" size="sm" onClick={limparFiltros} title="Limpar filtros" className="gap-1">
                  <X className="h-4 w-4" /> Limpar
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      aria-label="Selecionar todas as parcelas em aberto"
                      data-state={someSelected && !allSelected ? "indeterminate" : undefined}
                    />
                  </TableHead>
                  <Col campo="fornecedor" {...ordProps}>Fornecedor</Col>
                  <Col campo="documento"  {...ordProps}>Documento</Col>
                  <Col campo="parcela"    {...ordProps}>Parcela</Col>
                  <Col campo="vencimento" {...ordProps}>Vencimento</Col>
                  <Col campo="valorTotal" {...ordProps} alinharDireita>Valor Parcela</Col>
                  <Col campo="saldo"      {...ordProps} alinharDireita>Saldo</Col>
                  <Col campo="status"     {...ordProps}>Status</Col>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                  </TableRow>
                ) : parcelas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Nenhuma parcela encontrada</TableCell>
                  </TableRow>
                ) : parcelas.map((p) => {
                  const status  = calcularStatus(p.isPago, p.dtVencimento);
                  const checked = p.id > 0 && selectedIds.includes(p.id);
                  const podeOperarParcela = !p.semParcela && p.id > 0;
                  return (
                    <TableRow key={`${p.tituloId}-${p.id}`} data-selected={checked || undefined}>
                      <TableCell>
                        {!p.isPago && podeOperarParcela && (
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleOne(p.id)}
                            aria-label={`Selecionar parcela ${p.nroParcela} do título ${p.tituloId}`}
                          />
                        )}
                      </TableCell>
                      <TableCell className="font-medium" title={`Fornecedor #${p.fornecedorId}`}>
                        {p.fornecedorNome || `Forn. #${p.fornecedorId}`}
                      </TableCell>
                      <TableCell>{p.numDocumento || p.duplicata || `#${p.tituloId}`}</TableCell>
                      <TableCell>
                        {p.nroParcela}/{p.qtdParcelas}
                        {p.semParcela && (
                          <Badge
                            variant="outline"
                            className="ml-2 text-[10px]"
                            title="Título legado sem parcela registrada — a linha vem do cabeçalho. Use 'Quitar título' para pagar."
                          >
                            sem parcela
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{new Date(p.dtVencimento).toLocaleDateString("pt-BR")}</TableCell>
                      <TableCell className="text-right">{brl(p.valorTotal)}</TableCell>
                      <TableCell className="text-right font-medium">{brl(p.saldo)}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[status]}>
                          <span className="flex items-center gap-1">
                            {status === "pago" || status === "recebido"
                              ? <CheckCircle className="h-3 w-3" />
                              : status === "atrasado"
                              ? <AlertCircle className="h-3 w-3" />
                              : <Clock className="h-3 w-3" />}
                            {statusLabels[status]}
                          </span>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => setDetalheId(p.tituloId)}
                            title="Ver o título completo"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>

                          {/* Editar o TÍTULO. Aparece mesmo com pagamento: o diálogo
                              trava o parcelamento e libera a classificação (centro de
                              custo / conta contábil / tipo de despesas / documento),
                              que é o que o usuário corrige depois que o título foi pago. */}
                          {podeEditar && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => setEditandoTituloId(p.tituloId)}
                              title="Editar o título"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}

                          {/* Pagar ESTA parcela (aceita valor parcial). */}
                          {!p.isPago && podeOperarParcela && podeEditar && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => setBaixando(p)}
                              disabled={isBaixandoParcela}
                              title="Pagar esta parcela"
                            >
                              <CheckCheck className="h-4 w-4" />
                            </Button>
                          )}

                          {/* Quitar o TÍTULO inteiro — operação distinta. */}
                          {!p.tituloIsPago && podeEditar && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => setQuitandoTitulo(p)}
                              disabled={isQuitandoTitulo}
                              title={`Quitar o título inteiro (saldo ${brl(p.tituloSaldo)})`}
                            >
                              <Wallet className="h-4 w-4 text-blue-500" />
                            </Button>
                          )}

                          {/* Estorno vale também para a parcela PARCIALMENTE paga. */}
                          {(p.isPago || p.valorPago > 0) && podeOperarParcela && podeEditar && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => setEstornoParcelaId(p.id)}
                              disabled={isEstornandoParcela}
                              title={p.isPago
                                ? "Estornar o pagamento desta parcela"
                                : `Estornar o pagamento parcial (${brl(p.valorPago)})`}
                            >
                              <RotateCcw className="h-4 w-4 text-orange-500" />
                            </Button>
                          )}

                          {/* Excluir só enquanto NÃO há pagamento algum. */}
                          {podeExcluir && !p.isPago && p.valorPago <= 0 && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => setExcluindo(p)}
                              title="Excluir"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          {totalPaginas > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                Página {pagina} de {totalPaginas} ({total} parcelas)
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setPagina((p) => p - 1)} disabled={pagina <= 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Dialogs ── */}
      <BaixaParcelaPagarDialog
        parcela={baixando}
        onOpenChange={(o) => !o && setBaixando(null)}
        onConfirm={(parcelaId, req) =>
          baixarParcela({ parcelaId, req }, { onSuccess: () => setBaixando(null) })}
        isLoading={isBaixandoParcela}
      />

      {/* Quitação do TÍTULO: fecha TODAS as parcelas de uma vez, com data e forma. */}
      <QuitarTituloDialog
        open={!!quitandoTitulo}
        onOpenChange={(o) => !o && setQuitandoTitulo(null)}
        tipo="pagar"
        pessoaNome={quitandoTitulo?.fornecedorNome}
        saldo={quitandoTitulo?.tituloSaldo ?? 0}
        isLoading={isQuitandoTitulo}
        onConfirm={(req) => {
          if (quitandoTitulo) {
            quitarTitulo(
              { id: quitandoTitulo.tituloId,
                req: { dtPagamento: req.dtPagamento, valorPago: quitandoTitulo.tituloSaldo,
                       formaPagamentoId: req.formaPagamentoId } },
              { onSuccess: () => setQuitandoTitulo(null) },
            );
          }
        }}
      />

      <EstornoDialog
        open={!!estornoParcelaId}
        onOpenChange={(o) => !o && setEstornoParcelaId(null)}
        tipo="pagar"
        onConfirm={() => {
          if (estornoParcelaId)
            estornarParcela(estornoParcelaId, { onSuccess: () => setEstornoParcelaId(null) });
        }}
        isLoading={isEstornandoParcela}
      />

      {/* Excluir: a parcela quando ela existe; o título quando é a única/órfã. */}
      <DeleteConfirmDialog
        open={!!excluindo}
        onOpenChange={(o) => !o && setExcluindo(null)}
        onConfirm={() => {
          if (!excluindo) return;
          if (excluindo.semParcela || excluindo.qtdParcelas <= 1) excluirTitulo(excluindo.tituloId);
          else excluirParcela(excluindo.id);
          setExcluindo(null);
        }}
        title={
          excluindo && !excluindo.semParcela && excluindo.qtdParcelas > 1
            ? "Excluir parcela"
            : "Excluir conta a pagar"
        }
        description={
          excluindo && !excluindo.semParcela && excluindo.qtdParcelas > 1
            ? `A parcela ${excluindo.nroParcela}/${excluindo.qtdParcelas} será removida e as ` +
              `demais renumeradas. Esta ação não pode ser desfeita.`
            : "O título e suas parcelas serão excluídos. Esta ação não pode ser desfeita."
        }
      />

      <ContaPagarEditDialog
        tituloId={editandoTituloId}
        onOpenChange={(o) => !o && setEditandoTituloId(null)}
        onConfirm={handleUpdate}
        isLoading={isAtualizando}
      />

      <ContaPagarDrawer id={detalheId} onClose={() => setDetalheId(null)} />
    </div>
  );
};

export default ContasPagar;
